import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  Legend, ReferenceLine, Cell,
} from "recharts";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

// Styled components
const ChartTitle = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 15px;
  border: 1px solid #ccc;
  border-radius: 4px;
`;

const AveragePurchaseValueByRegionChart = ({ purchaseData = [], clientsData = [] }) => {
  const [chartData, setChartData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [avgCurrentAcrossRegions, setAvgCurrentAcrossRegions] = useState(0);

  // colors: current = purple, prev = red, avg = blue, max highlight = darker purple
  const COLOR_CURRENT = "#9848c7";
  const COLOR_PREV    = "#FF3B30";
  const COLOR_MAX     = "#6e2d9c";
  const COLOR_AVG     = "#1E90FF";

  const regionNameMapping = {
    MAZOWIECKIE: "mazowieckie", ŚLĄSKIE: "śląskie", PODKARPACKIE: "podkarpackie",
    LUBUSKIE: "lubuskie", ZACHODNIOPOMORSKIE: "zachodniopomorskie",
    WIELKOPOLSKIE: "wielkopolskie", DOLNOŚLĄSKIE: "dolnośląskie", ŁÓDZKIE: "łódzkie",
    ŚWIĘTOKRZYSKIE: "świętokrzyskie", POMORSKIE: "pomorskie", LUBELSKIE: "lubelskie",
    OPOLSKIE: "opolskie", PODLASKIE: "podlaskie", "WARMIŃSKO-MAZURSKIE": "warmińsko-mazurskie",
    "KUJAWSKO-POMORSKIE": "kujawsko-pomorskie", MAŁOPOLSKIE: "małopolskie",
  };

  // init default year/month
  useEffect(() => {
    if (!purchaseData.length) return;
    if (!selectedYear) {
      const years = purchaseData
        .map(r => parseInt(r.Rok))
        .filter(Number.isFinite)
        .sort((a, b) => b - a);
      setSelectedYear(years[0] || null);
    }
  }, [purchaseData, selectedYear]);

  useEffect(() => {
    if (!purchaseData.length || !clientsData.length || !selectedYear) return;

    const includeFullYear = selectedMonth === 0;

    // NIP -> region
    const nipToRegion = {};
    for (const client of clientsData) {
      const nip = client.nip?.trim();
      if (!nip) continue;
      const regionRaw = client.wojewodztwo?.trim().toUpperCase();
      const mapped = regionNameMapping[regionRaw];
      nipToRegion[nip] = mapped || regionRaw || "Unknown";
    }

    // helper: aggregate totals & counts for given period
    const aggregate = (year) => {
      const totals = {};
      const counts = {};

      for (const row of purchaseData) {
        const rok = parseInt(row.Rok);
        const miesiac = parseInt(row.Miesiac);
        if (rok !== year) continue;
        if (!includeFullYear && miesiac !== selectedMonth) continue;

        const nip = row.NrDostawcy?.trim();
        const region = nipToRegion[nip] || "Unknown";

        // value = K_42 + K_43 (zakupy convention)
        const amount =
          (Number(row.K_42) || 0) +
          (Number(row.K_43) || 0);

        if (!amount || region === "Unknown") continue;

        totals[region] = (totals[region] || 0) + amount;
        counts[region] = (counts[region] || 0) + 1;
      }

      // avg per region = total / count
      const avgPerRegion = {};
      for (const region of Object.keys(totals)) {
        avgPerRegion[region] = totals[region] / (counts[region] || 1);
      }
      return avgPerRegion;
    };

    const cur = aggregate(selectedYear);
    const prev = aggregate(selectedYear - 1);

    // union of regions to keep stable axis
    const allRegions = Array.from(new Set([...Object.keys(cur), ...Object.keys(prev)]));

    const rows = allRegions.map((region) => ({
      region,
      currentAvg: cur[region] || 0,
      prevAvg: prev[region] || 0,
    }));

    // compute average across regions (current) and find max to highlight
    const avgAcross =
      rows.length ? rows.reduce((s, r) => s + r.currentAvg, 0) / rows.length : 0;
    const maxCurrent = rows.length ? Math.max(...rows.map(r => r.currentAvg)) : 0;

    setAvgCurrentAcrossRegions(avgAcross);
    setChartData(
      rows
        .map(r => ({ ...r, isMax: r.currentAvg === maxCurrent && maxCurrent > 0 }))
        .sort((a, b) => b.currentAvg - a.currentAvg)
    );
  }, [purchaseData, clientsData, selectedYear, selectedMonth]);

  const getMonthName = (m) =>
    new Date(2000, m - 1).toLocaleString("pl-PL", { month: "long" });

  const availableYears = [...new Set(
    purchaseData.map(r => r.Rok).filter(y => typeof y === "number")
  )].sort((a, b) => b - a);

  const availableMonths = selectedYear
    ? [...new Set(
        purchaseData
          .filter(r => r.Rok === selectedYear)
          .map(r => r.Miesiac)
          .filter(m => typeof m === "number")
      )].sort((a, b) => a - b)
    : [];

  const prevYearLabel = selectedYear ? `r-1 (${selectedYear - 1})` : "r-1";

  return (
    <div style={{ margin: "20px auto", width: "90%" }}>
      <ChartTitle>
        Średnia Wartość Zakupu wg Województw
        <InfoIconWrapper data-tooltip-id="avg-purchases-tooltip">
          <Info />
        </InfoIconWrapper>
      </ChartTitle>
      <InfoTooltip
        id="avg-purchases-tooltip"
        place="left"
        content={
          <>
            <strong>Średnia wartość zakupu</strong><br />
            Pokazuje średnią wartość zakupu (K_42 + K_43) w podziale<br />
            na województwa, dla wybranego okresu, z porównaniem r/r.
          </>
        }
      />

      <Controls>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label>Rok:</label>
          <Select value={selectedYear || ""} onChange={(e) => setSelectedYear(parseInt(e.target.value))}>
            {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
          </Select>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <label>Miesiąc:</label>
          <Select value={selectedMonth} onChange={(e) => setSelectedMonth(parseInt(e.target.value))}>
            <option value={0}>Cały rok</option>
            {availableMonths.map(m => (
              <option key={m} value={m}>{getMonthName(m)}</option>
            ))}
          </Select>
        </div>
      </Controls>

      {chartData.length === 0 ? (
        <p style={{ marginTop: "20px", color: "#888" }}>Brak danych do wyświetlenia.</p>
      ) : (
        <ResponsiveContainer width="100%" height={520}>
          <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 24 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              tickFormatter={(val) => val.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            />
            <YAxis type="category" dataKey="region" width={170} />
            <Tooltip
              formatter={(val, key) => {
                const label = key === "currentAvg" ? "Bieżący okres" : "r-1";
                return [`${Number(val).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`, label];
              }}
            />

            {/* legend with fixed colors */}
            <Legend
              payload={[
                { value: `${selectedYear || ""}`, type: "square", color: COLOR_CURRENT, id: "legend-current" },
                { value: prevYearLabel, type: "square", color: COLOR_PREV, id: "legend-prev" },
                { value: "Średnia (bieżący)", type: "line", color: COLOR_AVG, id: "legend-avg" },
              ]}
            />

            {/* average across regions (current) */}
            <ReferenceLine
              x={avgCurrentAcrossRegions}
              stroke={COLOR_AVG}
              strokeDasharray="4 4"
              label={{ value: "Średnia (bieżący)", position: "top", fill: COLOR_AVG, fontSize: 12 }}
            />

            {/* prev year (red) */}
            <Bar dataKey="prevAvg" name={prevYearLabel} fill={COLOR_PREV} barSize={26} />

            {/* current year (purple) with max highlighted */}
            <Bar dataKey="currentAvg" name={`${selectedYear || ""}`} fill={COLOR_CURRENT} barSize={26}>
              {chartData.map((entry, i) => (
                <Cell key={`cell-${i}`} fill={entry.isMax ? COLOR_MAX : COLOR_CURRENT} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default AveragePurchaseValueByRegionChart;
